import { Box, SxProps, Theme, Typography } from '@mui/material';

import { Maybe } from '@/types';
import FocalPointImage from '@/components/FocalPointImage';

import { IBlogAuthorProps } from '../BlogAuthorDetails';
import { BLOG_AUTHOR_LABELS } from '../BlogDetailsPage.constants';
import { blogAuthorImageCtrStyles, blogAuthorImageStyles } from '../BlogDetailsPage.styles';

import { blogAuthorGridImageStyles, blogAuthorListGridStyles, blogAuthorStyles } from './BlogDetailsHero.styles';

interface BlogDetailsHeroAuthorListProps {
	author?: Maybe<IBlogAuthorProps>;
	reviewer?: Maybe<IBlogAuthorProps>;
	textColor?: Maybe<string>;
	theme: Theme;
}

const BlogDetailsHeroAuthorListItem = ({
	author,
	textColor,
	authorLabel = 'Authored By',
	sx,
}: {
	author: IBlogAuthorProps;
	textColor?: BlogDetailsHeroAuthorListProps['textColor'];
	authorLabel?: string;
	sx?: SxProps;
}) => {
	return (
		<Box
			sx={{
				...sx,
				display: 'flex',
				flexFlow: 'row no-wrap',
				gap: '.25rem',
			}}
		>
			<Box>
				<Typography variant="bodyMediumBold" color={textColor ?? ''} component="p">
					{authorLabel}
				</Typography>
				<Typography variant="bodyMediumBook" color={textColor ?? ''} component="p">
					{author.name && <span data-testid="blog_details_hero_author_name">{author.name}</span>}
					{author.title && <span data-testid="blog_details_hero_author_title"> {author.title}</span>}
				</Typography>
			</Box>
		</Box>
	);
};

export default function BlogDetailsHeroAuthorList({
	author,
	reviewer,
	textColor,
}: Readonly<BlogDetailsHeroAuthorListProps>) {
	if (!author && !reviewer) return <></>;
	const reviewerLabel = BLOG_AUTHOR_LABELS.MEDICALLY_REVIEWED_BY;
	const authorLabel = !reviewer ? `Authored & ${reviewerLabel}` : BLOG_AUTHOR_LABELS.AUTHORED_BY;
	const hasAuthorImage = !!author?.image;
	return (
		<Box sx={blogAuthorListGridStyles(hasAuthorImage)}>
			{hasAuthorImage && (
				<Box sx={{ ...blogAuthorImageCtrStyles, ...blogAuthorGridImageStyles }}>
					<FocalPointImage
						brandFolderImage={author?.image?.image}
						focalPoint={author?.image?.focalPoint}
						imageSx={blogAuthorImageStyles}
					/>
				</Box>
			)}
			{author && (
				<BlogDetailsHeroAuthorListItem
					author={author}
					textColor={textColor}
					authorLabel={authorLabel}
					sx={blogAuthorStyles}
				/>
			)}
			{reviewer && (
				<BlogDetailsHeroAuthorListItem author={reviewer} textColor={textColor} authorLabel={reviewerLabel} />
			)}
		</Box>
	);
}
